import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Copy, Eye, Send, Link2 } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

export function ReferralLinkCard() {
  const [link, setLink] = useState(null);
  useEffect(() => { api.get("/referral-partner/link").then((r) => setLink(r.data)).catch(() => setLink(null)); }, []);
  const copy = async () => {
    try { await navigator.clipboard.writeText(link.url); toast.success("Referral link copied"); }
    catch { toast.error("Copy failed — select the link and copy it manually"); }
  };
  if (!link) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm flex flex-wrap items-center gap-3" data-testid="rp-link-card">
      <Link2 className="w-5 h-5 text-[#006090]" />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Your personal referral link</p>
        <p className="text-sm text-slate-800 truncate" data-testid="rp-link-url">{link.url}</p>
      </div>
      <Button variant="outline" size="sm" onClick={copy} data-testid="rp-copy-link"><Copy className="w-4 h-4 mr-1.5" />Copy Referral Link</Button>
    </div>
  );
}

function SentList({ items }) {
  if (!items.length) return <p className="text-sm text-slate-500" data-testid="rp-invites-empty">No invitations sent yet.</p>;
  return (
    <div className="divide-y divide-slate-100" data-testid="rp-invites-list">
      {items.map((i) => (
        <div key={i.id} className="py-2 flex flex-wrap items-center gap-2 text-sm" data-testid={`rp-invite-${i.id}`}>
          <span className="font-medium text-slate-800">{[i.first_name, i.last_name].filter(Boolean).join(" ")}</span>
          <span className="text-slate-500">{i.email}</span>
          <span className="text-xs text-slate-400">{i.template_label} · {fmt(i.sent_at)}</span>
          <span className={`ml-auto rounded-full px-2 py-0.5 text-xs ${i.status === "Responded" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{i.status}</span>
        </div>
      ))}
    </div>
  );
}

export default function InviteCandidate() {
  const [templates, setTemplates] = useState([]);
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", template: "introduction", message: "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState([]);
  const set = (k, v) => { setF((p) => ({ ...p, [k]: v })); setPreview(null); };
  const loadSent = () => api.get("/referral-partner/invitations").then((r) => setSent(r.data.items || [])).catch(() => setSent([]));
  useEffect(() => {
    api.get("/referral-partner/invite-templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {});
    loadSent();
  }, []);
  const doPreview = async () => {
    try { const { data } = await api.post("/referral-partner/invitations/preview", f); setPreview(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const send = async () => {
    setBusy(true);
    try {
      await api.post("/referral-partner/invitations", f);
      toast.success("Invitation sent");
      setF({ first_name: "", last_name: "", email: "", template: f.template, message: "" }); setPreview(null); loadSent();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-4xl space-y-5" data-testid="rp-invite-page">
      <PageHeader title="Invite a Candidate" description="Send an approved invitation that includes your personal referral link. Responses are credited to you automatically." />
      <ReferralLinkCard />
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm grid sm:grid-cols-2 gap-3">
        <Input placeholder="First name *" value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="rp-inv-first" />
        <Input placeholder="Last name" value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="rp-inv-last" />
        <Input className="sm:col-span-2" type="email" placeholder="Email *" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="rp-inv-email" />
        <div className="sm:col-span-2 grid sm:grid-cols-3 gap-2" data-testid="rp-inv-templates">
          {templates.map((t) => (
            <button key={t.key} type="button" onClick={() => set("template", t.key)} data-testid={`rp-inv-tpl-${t.key}`}
              className={`text-left rounded-lg border p-3 text-sm transition-colors ${f.template === t.key ? "border-[#006090] bg-[#006090]/5 text-[#006090]" : "border-slate-200 hover:border-slate-300 text-slate-700"}`}>{t.label}</button>
          ))}
        </div>
        <Textarea className="sm:col-span-2" rows={3} maxLength={600} placeholder="Add a short personal note (optional)" value={f.message} onChange={(e) => set("message", e.target.value)} data-testid="rp-inv-message" />
        <p className="sm:col-span-2 text-xs text-slate-500">The template wording, required disclosure and your referral link are added automatically and can't be changed.</p>
        <div className="sm:col-span-2 flex gap-2">
          <Button variant="outline" onClick={doPreview} data-testid="rp-inv-preview"><Eye className="w-4 h-4 mr-1.5" />Preview</Button>
          <Button className="bg-[#006090] hover:bg-[#004E75]" disabled={!preview || busy} onClick={send} data-testid="rp-inv-send"><Send className="w-4 h-4 mr-1.5" />{busy ? "Sending…" : "Send Invitation"}</Button>
        </div>
        {preview && (
          <div className="sm:col-span-2 rounded-lg border border-slate-200 p-4 bg-slate-50" data-testid="rp-inv-preview-box">
            <p className="text-xs text-slate-500 mb-2">Subject: <strong className="text-slate-800">{preview.subject}</strong></p>
            <iframe title="Invitation preview" srcDoc={preview.html} sandbox="" className="w-full h-80 bg-white rounded border border-slate-200" data-testid="rp-inv-preview-frame" />
          </div>)}
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="font-semibold text-slate-900 mb-2">Invitations sent</h3>
        <SentList items={sent} />
      </div>
    </div>
  );
}
